import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, CircularProgress, Container, Link, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { Card, Pill, SectionHeader } from '../components/shared';
import ShareRequirement from '../components/profileshare/ShareRequirement';
import ShareLedger from '../components/profileshare/ShareLedger';
import ShareAskModal from '../components/profileshare/ShareAskModal';
import { startSession, askQuestion, recordEvent } from '../services/profileShareService';
import { SAGE, CREAM, WARM } from '../theme';

/**
 * Profile Share — the public page a recipient opens from their link.
 *
 * Their posting is the index: verbatim quotes are the headers, the sender's
 * evidence is the content. Two hard constraints from the spec hold throughout:
 * no scores, percentages, match bars or badges anywhere (they score it, not
 * us), and the page must be fully readable without a single click —
 * collapsibility adds depth, it is never the only path to substance.
 *
 * This renders OUTSIDE the authenticated app shell (see App.js): no sidebar, no
 * session, no account. The token in the URL is the only credential, and the
 * page must never behave as though the visitor is a user of Onward — they are
 * a guest of the sender's.
 *
 * See docs/design/profile-share.md and Profile_Share_Spec.md.
 */

const GENERAL_KEY = '__general__';

/**
 * Find the requirement or claim a thread key belongs to, so a ledger entry can
 * reopen its own conversation. Keys are claim ids where a claim was anchored
 * and requirement ids otherwise, which is exactly how the backend resolves
 * them — so one scan over the tabs matching either is enough.
 */
const findAnchorByThreadKey = (tabs, key) => {
  if (!key) return null;
  for (const tab of tabs) {
    for (const section of tab.sections || []) {
      for (const item of section.items || []) {
        const claim = (item.claims || []).find((c) => c.id === key);
        if (claim || item.id === key) {
          return {
            tabId: tab.id,
            modal: {
              key,
              anchor: {
                tab_id: tab.id,
                requirement_id: item.id,
                claim_id: claim ? claim.id : null,
              },
              quote: item.quote || '',
              sourceLabel: item.source_label || '',
              claim: claim ? claim.claim : '',
            },
          };
        }
      }
    }
  }
  return null;
};

const ProfileSharePage = () => {
  const { token } = useParams();

  const [state, setState] = useState({ status: 'loading', data: null });
  const [activeTabId, setActiveTabId] = useState(null);
  const [openReqs, setOpenReqs] = useState(new Set());
  const [openClaims, setOpenClaims] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [threads, setThreads] = useState({});
  const [ledger, setLedger] = useState([]);
  const [pending, setPending] = useState(false);
  const [mode, setMode] = useState('live');
  const started = useRef(false);

  // Keep the tokenised URL out of search indexes. Scoped to this page rather
  // than set globally so the rest of the site stays indexable. The Referer
  // header is handled separately, by a no-referrer meta in index.html — a
  // crawler directive would not have stopped the token leaking to any site the
  // visitor clicks through to.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const data = await startSession(token);
        setMode(data.chat?.mode || 'live');
        setActiveTabId(data.share?.default_tab || data.tabs?.[0]?.id || 'resume');
        // L1 expanded by default across every tab — collapsed, the page opens
        // as a list of the recipient's own posting lines with nothing under
        // them, which reads as pasting their JD back at them.
        setOpenReqs(new Set(
          (data.tabs || []).flatMap((t) =>
            (t.sections || []).flatMap((s) => (s.items || []).map((i) => i.id))
          )
        ));
        setState({ status: 'ready', data });
        document.title = `${data.profile?.name || 'Profile'} — for you`;
      } catch (e) {
        setState({ status: e.message === 'not_found' ? 'not_found' : 'error', data: null });
      }
    })();
  }, [token]);

  const data = state.data;
  const sessionId = data?.session_id;
  const tabs = useMemo(() => data?.tabs || [], [data]);
  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) || tabs[0],
    [tabs, activeTabId]
  );

  const track = useCallback((kind, payload) => {
    recordEvent(token, sessionId, kind, payload);
  }, [token, sessionId]);

  const onTabChange = (_e, id) => {
    setActiveTabId(id);
    track('tab_switched', { tab_id: id });
  };

  const toggleReq = (id) => {
    setOpenReqs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else { next.add(id); track('requirement_expand', { tab_id: activeTabId, requirement_id: id }); }
      return next;
    });
  };

  const toggleClaim = (claimId, requirementId) => {
    setOpenClaims((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else {
        next.add(claimId);
        track('claim_expand', { tab_id: activeTabId, requirement_id: requirementId, claim_id: claimId });
      }
      return next;
    });
  };

  /** Open the L3 conversation, anchored to a requirement or one of its claims. */
  const openModal = ({ requirementId, claimId }) => {
    const section = (activeTab?.sections || [])
      .find((s) => (s.items || []).some((i) => i.id === requirementId));
    const item = (section?.items || []).find((i) => i.id === requirementId);
    const claim = claimId
      ? (item?.claims || []).find((c) => c.id === claimId)
      : null;

    setModal({
      // Thread key per spec §6 — claim, else requirement, else general.
      key: claimId || requirementId || GENERAL_KEY,
      anchor: { tab_id: activeTabId, requirement_id: requirementId, claim_id: claimId || null },
      quote: item?.quote || '',
      sourceLabel: item?.source_label || '',
      claim: claim?.claim || '',
    });
    track('modal_open', { tab_id: activeTabId, requirement_id: requirementId, claim_id: claimId || null });
  };

  /**
   * Send a question. The reply lands in whichever thread asked it, and the
   * ledger records what happened — answered here, or going to the sender.
   */
  const ask = useCallback(async (key, text, anchor, anchorQuote) => {
    setThreads((t) => ({
      ...t,
      [key]: [...(t[key] || []), { id: `${key}-u-${Date.now()}`, role: 'user', content: text }],
    }));
    setPending(true);
    try {
      const r = await askQuestion(token, sessionId, text, anchor);
      const escalated = Boolean(r.escalated);
      const reply = r.answer
        || "That isn't covered in what's written here, so I've passed it on rather than guessing.";

      setThreads((t) => ({
        ...t,
        [key]: [...(t[key] || []), { id: `${key}-a-${Date.now()}`, role: 'assistant', content: reply }],
      }));
      setLedger((l) => [...l, {
        id: `q-${Date.now()}`,
        anchor: anchorQuote || null,
        q: text,
        // The answer is carried on the entry so the rail can show it. An
        // unanchored question has no modal to read the reply in, so without
        // this it would be answered invisibly — a state chip and nothing else.
        answer: escalated ? '' : reply,
        state: escalated ? 'escalated' : 'answered',
        threadKey: key === GENERAL_KEY ? null : key,
      }]);
      if (r.mode) setMode(r.mode);
    } catch (e) {
      setThreads((t) => ({
        ...t,
        [key]: [...(t[key] || []), {
          id: `${key}-e-${Date.now()}`,
          role: 'assistant',
          content: "Something went wrong sending that. Your question wasn't lost — try again in a moment.",
        }],
      }));
    } finally {
      setPending(false);
    }
  }, [token, sessionId]);

  const threadCounts = useMemo(() => {
    const counts = {};
    Object.entries(threads).forEach(([k, msgs]) => {
      counts[k] = msgs.filter((m) => m.role === 'user').length;
    });
    return counts;
  }, [threads]);

  if (state.status === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: CREAM[100] }}>
        <CircularProgress sx={{ color: SAGE[400] }} />
      </Box>
    );
  }

  if (state.status !== 'ready') {
    // Unknown and revoked links are the same 404 from the server, so say the
    // same thing for both — the page must not confirm a link ever existed.
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: CREAM[100], p: 3 }}>
        <Card sx={{ p: 4, maxWidth: 440, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", color: SAGE[800] }}>
            This link isn't active
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: SAGE[600], lineHeight: 1.7 }}>
            It may have been withdrawn, or the address may be incomplete. If someone
            sent you here, ask them for a fresh link.
          </Typography>
        </Card>
      </Box>
    );
  }

  const { profile = {}, share = {} } = data;
  const firstName = profile.name?.split(' ')[0] || 'the sender';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: CREAM[100], py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Main column takes the remaining width. The rail is a fixed narrow
              column rather than a proportional one, so the reading measure
              grows with the viewport instead of the ledger growing with it. */}
          <Card sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0, px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            {/* The note addressed to them — the thing that separates this from
                a resume. Captured per recipient when the link is minted. */}
            {share.recipient_note && (
              <Box
                sx={{
                  mb: 3, p: 2.5, borderRadius: 2,
                  bgcolor: CREAM[200], borderLeft: `3px solid ${WARM.accent}`,
                }}
              >
                {share.recipient_label && (
                  <Typography sx={{ fontSize: 11, color: SAGE[500], mb: 0.75 }}>
                    {share.recipient_label}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{ fontSize: 14.5, lineHeight: 1.7, color: SAGE[700], maxWidth: '64ch' }}
                >
                  {share.recipient_note}
                </Typography>
              </Box>
            )}

            <Typography variant="h4" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[800] }}>
              {profile.name}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: SAGE[500], mt: 0.5 }}>
              {profile.headline}
            </Typography>
            {profile.links?.length > 0 && (
              <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                {profile.links.map((l) => (
                  <Link
                    key={l.label}
                    href={l.url}
                    target="_blank"
                    // noreferrer is load-bearing: without it the tokenised URL
                    // leaks to every site the visitor clicks through to.
                    rel="noopener noreferrer"
                    sx={{ fontSize: 12.5, color: SAGE[500], textDecorationColor: SAGE[200] }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Stack>
            )}

            {/* Logistics are share-level, not per-tab: the same constraints are
                identical across a company's postings, and answering them in
                every tab makes the tailoring look cosmetic. */}
            {share.logistics?.length > 0 && (
              <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: CREAM[200] }}>
                <SectionHeader>Logistics</SectionHeader>
                <Stack spacing={1.25} sx={{ mt: 1 }}>
                  {share.logistics.map((l) => (
                    <Box key={l.id || l.requirement}>
                      <Typography sx={{ fontSize: 12.5, color: SAGE[500] }}>{l.requirement}</Typography>
                      <Typography sx={{ fontSize: 13.5, color: SAGE[700], lineHeight: 1.6 }}>{l.answer}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Tabs
              value={activeTab?.id || false}
              onChange={onTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mt: 3, borderBottom: `1px solid ${SAGE[200]}`,
                '& .MuiTab-root': {
                  textTransform: 'none', fontFamily: "'Outfit', sans-serif",
                  alignItems: 'flex-start', color: SAGE[500], minHeight: 56,
                },
                '& .Mui-selected': { color: `${SAGE[700]} !important` },
                '& .MuiTabs-indicator': { bgcolor: SAGE[500] },
              }}
            >
              {tabs.map((t) => (
                <Tab
                  key={t.id}
                  value={t.id}
                  label={
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: SAGE[400] }}>{t.sub}</Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>

            <Box sx={{ mt: 3 }}>
              {activeTab?.kind === 'resume' ? (
                <ResumeView resume={activeTab.resume} />
              ) : activeTab ? (
                <>
                  <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[800] }}>
                      {activeTab.role_title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: SAGE[400] }}>{activeTab.role_sub}</Typography>
                  </Stack>
                  {activeTab.opening && (
                    <Typography variant="body1" sx={{ mt: 1, fontSize: 14, lineHeight: 1.65, color: SAGE[600], maxWidth: '66ch' }}>
                      {activeTab.opening}
                    </Typography>
                  )}

                  {(activeTab.sections || []).map((section) => (
                    <Box key={section.id || section.title} sx={{ mt: 4 }}>
                      <Typography
                        sx={{
                          pb: 1, mb: 0.5, borderBottom: `2px solid ${SAGE[300]}`,
                          fontFamily: "'Outfit', sans-serif", fontSize: 12.5,
                          fontWeight: 600, color: SAGE[600],
                        }}
                      >
                        {section.title}
                      </Typography>
                      {(section.items || []).map((item) => (
                        <ShareRequirement
                          key={item.id}
                          item={item}
                          open={openReqs.has(item.id)}
                          onToggle={() => toggleReq(item.id)}
                          openClaims={openClaims}
                          onToggleClaim={toggleClaim}
                          threadCounts={threadCounts}
                          onAsk={openModal}
                        />
                      ))}
                    </Box>
                  ))}
                </>
              ) : null}
            </Box>

            <Box
              sx={{
                mt: 5, pt: 2, borderTop: `1px solid ${SAGE[100]}`,
                display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: SAGE[400] }}>
                Views and questions on this page are shared with {firstName}.
              </Typography>
              {share.as_of && (
                <Typography sx={{ fontSize: 11.5, color: SAGE[400] }}>
                  Current as of {share.as_of}
                </Typography>
              )}
            </Box>
          </Card>

          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 260px' }, minWidth: 0 }}>
            <ShareLedger
              ledger={ledger}
              isProcessing={pending}
              mode={mode}
              senderName={firstName}
              onAsk={(text) => ask(GENERAL_KEY, text, null, null)}
              onReopen={(entry) => {
                const found = findAnchorByThreadKey(tabs, entry.threadKey);
                if (found) { setActiveTabId(found.tabId); setModal(found.modal); }
              }}
            />
          </Box>
        </Box>
      </Container>

      <ShareAskModal
        anchor={modal}
        thread={(modal && threads[modal.key]) || []}
        isProcessing={pending}
        mode={mode}
        senderName={firstName}
        onClose={() => setModal(null)}
        onSend={(text) => ask(modal.key, text, modal.anchor, modal.quote)}
      />
    </Box>
  );
};

/**
 * The conventional resume, for a reader who wants the familiar shape.
 *
 * Built server-side from the sender's real Onward profile, so editing the
 * profile updates every share they've sent.
 */
const ResumeView = ({ resume }) => {
  const hasContent = resume && (
    (resume.experience || []).length
    || (resume.education || []).length
    || (resume.skills || []).length
    || resume.summary
  );

  if (!hasContent) {
    return (
      <Typography sx={{ fontSize: 13.5, color: SAGE[400], fontStyle: 'italic', lineHeight: 1.7 }}>
        This profile doesn't have any experience recorded yet. Anything added to the
        profile in Onward appears here automatically.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {resume.summary && (
        <Typography
          variant="body1"
          sx={{ fontSize: 14, lineHeight: 1.7, color: SAGE[700], maxWidth: '68ch' }}
        >
          {resume.summary}
        </Typography>
      )}

      {(resume.experience || []).map((job, i) => (
        <Box key={`${job.company}-${i}`}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: 14.5, color: SAGE[800] }}>
              {job.title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: SAGE[500] }}>{job.company}</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 12, color: SAGE[400] }}>{job.dates}</Typography>
          </Stack>
          <Stack component="ul" spacing={0.5} sx={{ mt: 0.75, pl: 2.5, mb: 0 }}>
            {(job.bullets || []).map((b, j) => (
              <Typography key={j} component="li" sx={{ fontSize: 13.5, lineHeight: 1.65, color: SAGE[700] }}>
                {b}
              </Typography>
            ))}
          </Stack>
        </Box>
      ))}

      {(resume.projects || []).length > 0 && (
        <Box>
          <SectionHeader>Projects &amp; writing</SectionHeader>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {resume.projects.map((p) => (
              <Box key={p.title}>
                {p.url ? (
                  <Link
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: 13.5, fontWeight: 600, color: SAGE[700], textDecorationColor: SAGE[200] }}
                  >
                    {p.title}
                  </Link>
                ) : (
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: SAGE[700] }}>
                    {p.title}
                  </Typography>
                )}
                {p.description && (
                  <Typography sx={{ fontSize: 13, color: SAGE[600], lineHeight: 1.6 }}>
                    {p.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {(resume.education || []).length > 0 && (
        <Box>
          <SectionHeader>Education</SectionHeader>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {resume.education.map((e, i) => (
              <Stack key={`${e.institution}-${i}`} direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13.5, color: SAGE[700] }}>{e.degree}</Typography>
                <Typography sx={{ fontSize: 13, color: SAGE[500] }}>{e.institution}</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 12, color: SAGE[400] }}>{e.year}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {(resume.skills || []).length > 0 && (
        <Box>
          <SectionHeader>Skills</SectionHeader>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            {resume.skills.map((s) => <Pill key={s} label={s} />)}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default ProfileSharePage;
